import React from 'react';
import { strings } from '../../resources/strings';
import { SeparationType } from '../../enums/SeparationType';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import { PrescriptionOption } from '../../enums/PrescriptionOption';
import { OrderGeneralOption } from '../../enums/OrderGeneralOption';
import { DataSource } from '../../enums/DataSource';
import { AssemblyDisapprovalType } from '../../enums/AssemblyDisapprovalType';
import { OrderForListing } from '../../models/OrderForListing';
import { SaveStateLogInput } from '../../services/StatesOrders/SaveStateLog/SaveStateLogInput';
import { SaveStatePrescriptionLogInput } from '../../services/StatesPrescriptions/SaveStatePrescriptionLog/SaveStatePrescriptionLogInput';
import { GenerateTSOReportInput } from '../../services/Reports/GenerateTSOReport/GenerateTSOReportInput';
import { useAuth } from '../../contexts/AuthContext';
import ServicesContext from '../../contexts/ServicesContext';
import { useOrderUtils } from '../../hooks/useOrderUtils';
import { RestrictionHelper } from '../../helpers/RestrictionHelper';
import { RestrictionType } from '../../enums/RestrictionType';
import { TSOBarcodeMode } from '../../enums/TSOBarcodeMode';
import { Table } from '../../common-components/Table/Table';
import { ModalRecipes } from '../ModalRecipes/ModalRecipes';
import { ModalNotificationsV2 } from '../ModalNotificationsV2/ModalNotificationsV2';
import { ModalDisapproveDegree } from '../ModalDisapproveDegree/ModalDisapproveDegree';
import { ModalHistory } from '../ModalHistory/ModalHistory';
import { ModalManageCarrier } from '../ModalManageCarrier/ModalManageCarrier';
import { ModalListNFes } from '../ModalListNFes/ModalListNFes';
import { ModalOrderDetails } from '../ModalOrderDetails/ModalOrderDetails';
import { ModalOrderNotes } from '../ModalOrderNotes/ModalOrderNotes';
import { ModalRecipesV2 } from '../ModalRecipesV2/ModalRecipesV2';
import { ModalRecipesForPhysicalStore } from '../ModalRecipesForPhysicalStore/ModalRecipesForPhysicalStore';
import { ModalManageLensCuttingIntegration } from '../ModalManageLensCuttingIntegration/ModalManageLensCuttingIntegration';
import { useOrdersTable } from './useOrdersTable';
import { OrdersTableHeader } from './OrdersTableHeader';
import { OrdersTableRow } from './OrdersTableRow';
import { ContainerList, ContainerListHeader } from './styles';

interface IOrdersProps {
  ordersForListing?: OrderForListing[];
  selectOrder?: (orderId: number) => void;
  selectAllOrder?: () => void;
  separationType: SeparationType;
  filterAreaHeight: number;
  refreshOrder?: (orderId?: number) => void;
  expressOrder?: boolean;
}

const Orders: React.FC<IOrdersProps> = ({
  ordersForListing,
  selectOrder,
  selectAllOrder,
  separationType,
  filterAreaHeight,
  refreshOrder,
  expressOrder
}) => {

  const { session } = useAuth();
  const [modalOrderId, setModalOrderId] = React.useState<number | undefined>(undefined);
  const [modalOrderNumber, setModalOrderNumber] = React.useState<string | undefined>(undefined);
  const [modalRecipesVisible, setModalRecipesVisible] = React.useState<boolean>(false);
  const [modalRecipesReadOnly, setModalRecipesReadOnly] = React.useState<boolean>(false);
  const [modalRecipesOrderDataSource, setModalRecipesOrderDataSource] = React.useState<DataSource | undefined>(undefined);
  const [modalRecipesV2Visible, setModalRecipesV2Visible] = React.useState<boolean>(false);
  const [modalRecipesForPhysicalStoreVisible, setModalRecipesForPhysicalStoreVisible] = React.useState<boolean>(false);
  const [modalNotificationsVisible, setModalNotificationsVisible] = React.useState<boolean>(false);
  const [modalNotificationsOrderEmail, setModalNotificationsOrderEmail] = React.useState<string | undefined>(undefined);
  const [modalNotificationsOrderPhone, setModalNotificationsOrderPhone] = React.useState<string | undefined>(undefined);
  const [modalDisapproveVisible, setModalDisapproveVisible] = React.useState<boolean>(false);
  const [modalHistoryOrderVisible, setModalHistoryOrderVisible] = React.useState<boolean>(false);
  const [modalManageCarrierVisible, setModalManageCarrierVisible] = React.useState<boolean>(false);
  const [modalListNFesVisible, setModalListNFesVisible] = React.useState<boolean>(false);
  const [modalOrderDetailsVisible, setModalOrderDetailsVisible] = React.useState<boolean>(false);
  const [modalOrderNotesVisible, setModalOrderNotesVisible] = React.useState<boolean>(false);
  const [modalManageLensCuttingIntegration, setModalManageLensCuttingIntegration] = React.useState<any>({
    visible: false,
    orderId: undefined,
    orderNumber: undefined,
  });

  const { getWidthColumns, getVisibleColumns } = useOrdersTable();
  const {
    registerAssemblyDisapproval,
    stopChangeIfOrderForListingHasExternallyChanged,
    orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected
  } = useOrderUtils();

  const {
    saveStateLogService,
    saveStatePrescriptionLogService,
    toastService,
    alertService,
    generateTSOReportService,
    getDigitalCertificatePageLinkService
  } = React.useContext(ServicesContext);

  const selectedAll = getSelectedAll();

  const widthColumn = React.useMemo(() => getWidthColumns(separationType, expressOrder), [separationType, expressOrder]);

  const rowsRender = React.useMemo(() => {
    const render = ordersForListing?.map(orderForListing => {
      return (
        <OrdersTableRow
          key={`order-${orderForListing.order.id}`}
          separationType={separationType}
          orderForListing={orderForListing}
          widthColumn={widthColumn}
          visibleColumns={getVisibleColumns(widthColumn)}
          selectOrder={selectOrder}
          userProfile={session?.user?.profileType}
          changeState={changeState}
          handleGeneralOption={handleGeneralOption}
          handlePrescriptionOption={handlePrescriptionOption}
          statePrescriptionChanged={() => handleRefreshOrder(orderForListing.order.id)}
          stateOrderChanged={() => handleRefreshOrder(orderForListing.order.id)}
          refresh={() => handleRefreshOrder(orderForListing.order.id)}
          expressOrder={expressOrder}
        />
      );
    }) || [];
    return render;
  }, [ordersForListing, separationType, widthColumn, selectOrder, session, expressOrder]);

  return (
    <>
      <ContainerListHeader>
        <Table>
          <thead>
            <OrdersTableHeader
              selectedAll={selectedAll}
              separationType={separationType}
              selectAllOrder={selectAllOrder}
              expressOrder={expressOrder}
            />
          </thead>
        </Table>
      </ContainerListHeader>
      <ContainerList filterAreaHeight={filterAreaHeight}>
        <Table>
          <tbody>
            {rowsRender}
          </tbody>
        </Table>
      </ContainerList>
      <ModalRecipes
        visible={modalRecipesVisible}
        readonly={modalRecipesReadOnly}
        close={(orderId) => closeModalRecipes(orderId)}
        title={getModalTitle(strings.Recipes)}
        orderId={modalOrderId}
        orderNumber={modalOrderNumber}
        orderDataSource={modalRecipesOrderDataSource}
        orderIsPhysicalStore={ordersForListing?.find(xo => xo.order.id === modalOrderId)?.order.isPhysicalStore || false}
        customerId={ordersForListing?.find(xo => xo.order.id === modalOrderId)?.order.customerId}
      />
      <ModalRecipesV2
        visible={modalRecipesV2Visible}
        readonly={modalRecipesReadOnly}
        close={(orderId) => closeModalRecipes(orderId)}
        title={getModalTitle(strings.Recipes)}
        orderId={modalOrderId}
        orderNumber={modalOrderNumber}
        orderDataSource={modalRecipesOrderDataSource}
        customerShopifyId={ordersForListing?.find(ofl => ofl.order.id === modalOrderId)?.order.customer?.shopifyId}
        customerId={ordersForListing?.find(ofl => ofl.order.id === modalOrderId)?.order.customerId}
      />
      <ModalRecipesForPhysicalStore
        visible={modalRecipesForPhysicalStoreVisible}
        readonly={modalRecipesReadOnly}
        close={(orderId) => closeModalRecipes(orderId)}
        title={getModalTitle(strings.Recipes)}
        orderId={modalOrderId}
        orderNumber={modalOrderNumber}
        customerId={ordersForListing?.find(ofl => ofl.order.id === modalOrderId)?.order.customerId}
        customerShopifyId={ordersForListing?.find(ofl => ofl.order.id === modalOrderId)?.order.customer?.shopifyId}
        isCrossChannelSale={ordersForListing?.find(ofl => ofl.order.id === modalOrderId)?.order.isCrossChannelSale}
        currentStatePrescriptionId={ordersForListing?.find(ofl => ofl.order.id === modalOrderId)?.statePrescriptionLog?.currentStateId}
      />
      <ModalNotificationsV2
        visible={modalNotificationsVisible}
        close={(orderId) => closeModalNotifications(orderId)}
        title={getModalTitle(strings.Notifications)}
        orderId={modalOrderId}
        orderNumber={modalOrderNumber}
        orderEmail={modalNotificationsOrderEmail}
        orderPhone={modalNotificationsOrderPhone}
      />
      {modalDisapproveVisible ?
        <ModalDisapproveDegree
          visible={modalDisapproveVisible}
          orderId={modalOrderId}
          close={() => closeModalDisapprove()}
          registerAssemblyDisapproval={(orderId, problemType, problemDetails) => handleAssemblyDisapproval(orderId, problemType, problemDetails)}
        /> : null}
      <ModalHistory
        visible={modalHistoryOrderVisible}
        orderId={modalOrderId}
        close={() => closeModalHistory()}
        title={getModalTitle(strings.Historic)}
      />
      <ModalManageCarrier
        visible={modalManageCarrierVisible}
        orderId={modalOrderId}
        orderNumber={modalOrderNumber}
        close={() => closeModalManageCarrier()}
        title={getModalTitle(strings.ManageTransport)}
      />
      <ModalListNFes
        visible={modalListNFesVisible}
        orderId={modalOrderId}
        close={() => closeModalListNFes()}
        title={getModalTitle(strings.ListNFes)}
      />
      <ModalOrderDetails
        visible={modalOrderDetailsVisible}
        orderId={modalOrderId}
        close={() => closeModalOrderDetails()}
        title={`${strings.OrderDetails}`}
      />
      <ModalOrderNotes
        visible={modalOrderNotesVisible}
        close={() => closeModalOrderNotes()}
        title={getModalTitle(strings.OrderNotes)}
        orderId={modalOrderId}
      />
      {modalManageLensCuttingIntegration.visible ? (
        <ModalManageLensCuttingIntegration
          orderId={modalOrderId}
          orderNumber={modalOrderNumber}
          close={() => closeModalManageLensCuttingIntegration()}
        />
      ) : null}
    </>
  );

  function getSelectedAll() {
    if (!ordersForListing || ordersForListing.length === 0) return false;
    return ordersForListing.every(orderForListing =>
      orderForListing._selected === true ||
      (orderForListing.order.isCrossChannelSale &&
        !orderIsCrossChannelFulfilledByEcommerceAndWithPhysicalStoreOriginAndCanBeSelected(orderForListing.order))
    );
  }

  function changeState(orderForListing: OrderForListing, id: any) {
    if (stopChangeIfOrderForListingHasExternallyChanged(orderForListing)) {
      return;
    }
    var input = new SaveStateLogInput();
    input.orderId = orderForListing.order.id;
    input.newStateId = id;
    input.userId = session?.user?.id || 0;
    saveStateLogService.handle(input)
      .then(result => {
        if (!result?.value?.result) return;
        handleRefreshOrder(orderForListing.order.id);
      })
      .catch(error => {
        if (error.response) {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
          return;
        }
        alertService.warning(strings.TheServerIsNotRespondingRefreshListAndTryAgain);
      });
  }

  function handlePrescriptionOption(orderForListing: OrderForListing, key: any) {
    if (key === PrescriptionOption.Recipes) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalRecipesVisible(true);
      setModalRecipesReadOnly(false);
      setModalRecipesOrderDataSource(orderForListing.order.dataSource);
      return;
    }

    if (key === PrescriptionOption.RecipesV2) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalRecipesV2Visible(true);
      setModalRecipesReadOnly(false);
      setModalRecipesOrderDataSource(orderForListing.order.dataSource);
      return;
    }

    if (key === PrescriptionOption.RecipesForPhysicalStore) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalRecipesForPhysicalStoreVisible(true);
      setModalRecipesReadOnly(false);
      setModalRecipesOrderDataSource(orderForListing.order.dataSource);
      return;
    }

    if (key === PrescriptionOption.NotificationsV2) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalNotificationsVisible(true);
      setModalNotificationsOrderEmail(orderForListing.order.customer?.email);
      setModalNotificationsOrderPhone(orderForListing.order.customer?.phone);
      return;
    }

    if (stopChangeIfOrderForListingHasExternallyChanged(orderForListing)) {
      return;
    }

    if (key === PrescriptionOption.RequestLens) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoLente, orderForListing.order.id);
      return;
    }

    if (key === PrescriptionOption.RequestInternalManufacturing) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoMontagemInterna, orderForListing.order.id);
      return;
    }

    if (key === PrescriptionOption.RequestExternalManufacturing) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoMontagemExterna, orderForListing.order.id);
      return;
    }

    if (key === PrescriptionOption.StartChecking) {
      saveNewStatePrescription(StatePrescriptionIdentification.AguardandoConferencia, orderForListing.order.id);
      return;
    }

    if (key === PrescriptionOption.Approve) {
      saveNewStatePrescription(StatePrescriptionIdentification.Ok, orderForListing.order.id);
      return;
    }

    if (key === PrescriptionOption.Disapprove) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalDisapproveVisible(true);
      return;
    }
  }

  function handleGeneralOption(orderForListing: OrderForListing, key: any) {
    if (key === OrderGeneralOption.Historic) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalHistoryOrderVisible(true);
      return;
    }
    if (key === OrderGeneralOption.PrintTSO) {
      printTSO(orderForListing.order.id);
      return;
    }
    if (key === OrderGeneralOption.PrintTSOV2A5) {
      printTSO(orderForListing.order.id, 2);
      return;
    }
    if (key === OrderGeneralOption.PrintTSOCssBarcode) {
      printTSO(orderForListing.order.id, 0, TSOBarcodeMode.Css);
      return;
    }
    if (key === OrderGeneralOption.PrintTSOV2A5CssBarcode) {
      printTSO(orderForListing.order.id, 2, TSOBarcodeMode.Css);
      return;
    }
    if (key === OrderGeneralOption.Recipes) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalRecipesForPhysicalStoreVisible(true);
      setModalRecipesReadOnly(true);
      setModalRecipesOrderDataSource(orderForListing.order.dataSource);
      return;
    }
    if (key === OrderGeneralOption.RecipesV2) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalRecipesV2Visible(true);
      setModalRecipesReadOnly(false);
      setModalRecipesOrderDataSource(orderForListing.order.dataSource);
      return;
    }
    if (key === OrderGeneralOption.ManageTransport) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalManageCarrierVisible(true);
      return;
    }
    if (key === OrderGeneralOption.ListNFes) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalListNFesVisible(true);
      return;
    }
    if (key === OrderGeneralOption.OrderDetails) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalOrderDetailsVisible(true);
      return;
    }
    if (key === OrderGeneralOption.OrderNotes) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalOrderNotesVisible(true);
      return;
    }
    if (key === OrderGeneralOption.DigitalLensCertificates) {
      getDigitalCertificatePageLinkService.handle({ orderId: orderForListing.order.id })
        .then(result => {
          if (!result?.value?.link) {
            alertService.warning(result?.value?.message || strings.OpsSomethingWrongGettingTheData);
            return;
          }
          window.open(result?.value?.link, '_blank');
        })
        .catch(error => {
          toastService.warning(strings.OpsSomethingWrongGettingTheData);
        });
      return;
    }
    if (key === OrderGeneralOption.ManageLensCuttingMachineIntegration) {
      openModal(orderForListing.order.id, orderForListing.order.number);
      setModalManageLensCuttingIntegration({
        visible: true,
        orderId: orderForListing.order.id,
        orderNumber: orderForListing.order.number,
      });
      return;
    }
  }

  function saveNewStatePrescription(statePrescriptionIdentification: StatePrescriptionIdentification, orderId: number) {
    if (
      statePrescriptionIdentification > StatePrescriptionIdentification.SolicitarLente &&
      RestrictionHelper.hasRestriction(session, RestrictionType.RequestLenses)
    ) {
      alertService.warning(RestrictionHelper.getRestrictionMessage(session, RestrictionType.RequestLenses, strings.RequestLensRestrictionGenericReason));
      return;
    }

    const input = new SaveStatePrescriptionLogInput();
    input.newStatePrescriptionId = statePrescriptionIdentification;
    input.orderId = orderId;
    input.userId = session?.user?.id;
    saveStatePrescriptionLogService.handle(input)
      .then(result => {
        if (!result?.value?.result) return;
        handleRefreshOrder(orderId);
      })
      .catch(error => {
        if (error.response) {
          toastService.warning(strings.OpsSomethingWrongSendTheData);
          return;
        }
        alertService.warning(strings.TheServerIsNotRespondingRefreshListAndTryAgain);
      });
  }

  function closeModalRecipes(orderId?: number) {
    closeModal();
    setModalRecipesVisible(false);
    setModalRecipesV2Visible(false);
    setModalRecipesForPhysicalStoreVisible(false);
    setModalRecipesReadOnly(false);
    handleRefreshOrder(orderId);
  }

  function closeModalNotifications(orderId?: number) {
    closeModal();
    setModalNotificationsVisible(false);
  }

  function closeModalDisapprove() {
    closeModal();
    setModalDisapproveVisible(false);
  }

  function closeModalHistory() {
    closeModal();
    setModalHistoryOrderVisible(false);
  }

  function closeModalManageCarrier() {
    closeModal();
    setModalManageCarrierVisible(false);
  }

  function closeModalListNFes() {
    const orderId = modalOrderId;
    closeModal();
    setModalListNFesVisible(false);
    handleRefreshOrder(orderId);
  }

  function closeModalOrderDetails() {
    const orderId = modalOrderId;
    closeModal();
    setModalOrderDetailsVisible(false);
    handleRefreshOrder(orderId);
  }

  function closeModalOrderNotes() {
    closeModal();
    setModalOrderNotesVisible(false);
  }

  function openModal(orderId?: number, number?: string) {
    setModalOrderId(orderId);
    setModalOrderNumber(number);
  }

  function closeModal() {
    setModalOrderId(undefined);
    setModalOrderNumber(undefined);
  }

  function printTSO(orderId?: number, version?: number, barcodeMode?: TSOBarcodeMode) {
    if (!orderId) {
      toastService.warning(strings.MessageToPrintingSelectedOrdersEmpty);
      return;
    }
    const input = new GenerateTSOReportInput();
    input.orderIds = [orderId];
    input.barcodeMode = barcodeMode;
    generateTSOReportService.handle(input, version);
  }

  function getModalTitle(prefix: string) {
    return prefix + (modalOrderNumber?.startsWith('L') ? ' ' : ' #') + modalOrderNumber;
  }

  function handleRefreshOrder(orderId?: number) {
    if (!orderId) return;
    refreshOrder && refreshOrder(orderId);
  }

  function handleAssemblyDisapproval(orderId: number, problemType: AssemblyDisapprovalType, problemDetails: string) {
    registerAssemblyDisapproval(orderId, problemType, problemDetails, () => {
      closeModalDisapprove();
      handleRefreshOrder(orderId);
    });
  }

  function closeModalManageLensCuttingIntegration() {
    closeModal();
    setModalManageLensCuttingIntegration({
      visible: false,
      orderId: undefined,
      orderNumber: undefined,
    });
  }
}

export { Orders };
